import React from 'react';
import { useHistory } from 'react-router-dom';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import BrightnessHighIcon from '@mui/icons-material/BrightnessHigh';

interface ICard {
  database: any;
  isProcessing?: boolean;
  isInteractionDisabled?: boolean;
}

function MiniGauge({ isLoading }: { isLoading?: boolean }) {
  if (!isLoading) return null;

  const r = 10;
  const circumference = 2 * Math.PI * r;
  const arc = circumference * 0.75;

  return (
    <svg
      className='bdb-nc-gauge is-spinning'
      width={26}
      height={26}
      viewBox='0 0 26 26'
    >
      <circle
        cx='13'
        cy='13'
        r={r}
        fill='none'
        stroke='var(--bg-border-db, #e2e8f0)'
        strokeWidth='2.5'
      />
      <circle
        cx='13'
        cy='13'
        r={r}
        fill='none'
        stroke='var(--action-color, #4895ef)'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeDasharray={`${arc} ${circumference - arc}`}
      />
    </svg>
  );
}

const CardDB: React.FC<ICard> = ({ database, isProcessing }) => {
  const history = useHistory();
  const getFormattedDate = () => {
    if (!database.last_screen_timestamp) return 'Never updated';
    const date = new Date(database.last_screen_timestamp);
    return `Updated ${date.toLocaleDateString()}`;
  };

  const handleClick = () => {
    if (isProcessing) return;
    history.push(`/database/${database.id}`);
  };

  return (
    <div
      className='bdb-nc-db-card'
      onClick={handleClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className='bdb-nc-db-header'>
        <div
          className={`bdb-nc-db-header-left${
            database.description ? ' has-desc' : ''
          }`}
        >
          <div className='bdb-nc-db-icon-box'>
            <Inventory2OutlinedIcon sx={{ color: 'var(--action-color)' }} />
          </div>
          <div className='bdb-nc-db-title-area'>
            <div className='bdb-nc-db-name-row'>
              <span className='bdb-nc-db-name' title={database.name}>
                {database.name}
              </span>
              <span className='bdb-nc-badge bdb-nc-badge-type-products'>
                <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
                Import
              </span>
            </div>
            {database.description && (
              <p className='bdb-nc-db-desc' title={database.description}>
                {database.description}
              </p>
            )}
          </div>
        </div>
        <MiniGauge isLoading={isProcessing} />
      </div>
      <div className='bdb-nc-db-content'>
        {/* Stats Grid - Using the 2-column layout from the image */}
        <div className='bdb-nc-db-stats-grid'>
          <div key={database.id} className='bdb-nc-stat-tile'>
            <div className='bdb-nc-tile-label'>
              <Inventory2OutlinedIcon
                sx={{ fontSize: 20, color: 'var(--action-color)' }}
              />
              Products
            </div>
            <p className='bdb-nc-tile-value'>{database.nr_products}</p>
          </div>
          <div key={database.id} className='bdb-nc-stat-tile'>
            <div className='bdb-nc-tile-label'>
              <BrightnessHighIcon
                style={{ width: 14, color: 'var(--full-bar-color)' }}
              />{' '}
              Assemblies
            </div>
            <p className='bdb-nc-tile-value'>
              {database.total_assemblies || 1}
            </p>
          </div>
          <div key={database.id} className='bdb-nc-stat-tile'>
            <div className='bdb-nc-tile-label'>
              <ReportProblemOutlinedIcon
                sx={{ fontSize: 14, color: 'var(--alert-text-color)' }}
              />
              Issues
            </div>
            <p className='bdb-nc-tile-value'>
              {database.total_issues_products || 0}
            </p>
          </div>
        </div>

        {/* Footer Section */}
        <div className='card-db-footer'>
          <div className='card-db-footer-row'>
            <span
              className={
                database.compliance_score >= 90.0
                  ? 'run-badge completed'
                  : database.compliance_score >= 60.0
                  ? 'run-badge needs-review'
                  : 'run-badge failed'
              }
            >
              {database.compliance_score?.toFixed(1)}%
            </span>
            <p className='bdb-nc-db-updated'>{getFormattedDate()}</p>
          </div>
        </div>

        {isProcessing && (
          <div className='card-db-processing-overlay'>
            <div className='card-db-spinner' />
          </div>
        )}
      </div>
    </div>
  );
};

export default CardDB;
